import { AlertTriangle, XCircle } from 'lucide-react'
import { useLocation } from 'wouter'
import { ago, costLike, fmt } from '../lib/format'
import type { ChangesetBrief } from '../lib/types'
import { Author, SourceIcon, StatusPill } from './ui'

/** Tests only speak up when something's wrong: passing is the normal case and needs no ink. */
function Tests({ t }: { t: ChangesetBrief['tests'] }) {
  if (!t) return null
  if (t.blockingFailures > 0)
    return (
      <span className="row small neg" style={{ gap: 4 }}>
        <XCircle size={13} />
        {t.blockingFailures} test{t.blockingFailures === 1 ? '' : 's'} failing
      </span>
    )
  if (t.warnings > 0)
    return (
      <span className="row small x-warn" style={{ gap: 4 }}>
        <AlertTriangle size={13} />
        {t.warnings} warning{t.warnings === 1 ? '' : 's'}
      </span>
    )
  return null
}

function Headline({ k }: { k: ChangesetBrief['kpis'] }) {
  // the biggest move; ties (opex +X is EBITDA −X) go to the KPI listed first, so rows compare like with like
  const list = (k ?? []).filter(x => Math.abs(x.delta) > 1e-6)
  const max = Math.max(0, ...list.map(x => Math.abs(x.delta)))
  const top = list.find(x => Math.abs(x.delta) >= max * 0.995)
  if (!top) return <span className="faint small">no KPI change</span>
  const good = costLike(top.key, top.label) ? top.delta < 0 : top.delta > 0
  return (
    <span className="small">
      <span className="muted">{top.label}</span> <span className={good ? 'pos' : 'neg'}>{fmt(top.delta, top.format, true)}</span>
    </span>
  )
}

export function ChangesetList({ items, empty = 'Nothing here.' }: { items: ChangesetBrief[]; empty?: string }) {
  const [, navigate] = useLocation()
  if (!items.length)
    return (
      <div className="empty small" style={{ padding: 24 }}>
        {empty}
      </div>
    )
  return (
    <table className="t cs-list">
      <tbody>
        {items.map(cs => (
          <tr key={cs.id} className="click" onClick={() => navigate(`/changesets/${cs.id}`)}>
            <td style={{ width: 22 }}>
              <SourceIcon source={cs.source} />
            </td>
            <td>
              <div className="strong" style={{ fontWeight: 500 }}>
                {cs.title}
              </div>
              <div className="small muted row wrap" style={{ gap: '2px 6px', marginTop: 2 }}>
                <Author a={cs.author} />
                <span className="faint">· {ago(cs.updatedAt)}</span>
                {cs.scenario && <span className="pill blue">scenario</span>}
                {cs.owners.length > 0 && (
                  <span className="faint">
                    · {cs.owners.slice(0, 3).join(', ')}
                    {cs.owners.length > 3 ? '…' : ''}
                  </span>
                )}
              </div>
              {cs.invalid && (
                <div className="small neg" style={{ marginTop: 2 }}>
                  {cs.invalid}
                </div>
              )}
            </td>
            <td className="nowrap-wide">
              <Headline k={cs.kpis} />
            </td>
            <td className="nowrap-wide">
              <Tests t={cs.tests} />
            </td>
            <td className="r">
              <StatusPill status={cs.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
